/**
 * The real `PlatformApiClient` implementation — genuine `fetch` calls against
 * `/api/v1/platform/*` (`backend/app/api/v1/platform/`), the platform-operator realm that
 * now actually exists server-side (§9.1). Wired up by default in `main.tsx`; see
 * `env.ts`'s `PLATFORM_API_MODE` for how to opt back into `mockPlatformApiClient` instead.
 *
 * Every request/response shape below was read directly from
 * `backend/app/schemas/platform.py` (the real Pydantic models), not assumed from this app's
 * own `api/types.ts` or any summary doc — see this file's per-method comments and this app's
 * ARCHITECTURE.md for the exact shape differences from the mock client this replaces:
 *
 *   - Login is two real HTTP calls. `POST /auth/login` returns `{challenge_id, expires_at}`
 *     where `challenge_id` IS a short-lived `aud:"platform", type:"mfa_challenge"` JWT (no
 *     server-side challenge store) — never a `demo_code`. `POST /auth/mfa/verify` takes a
 *     REAL 6-digit TOTP code (from an authenticator app seeded by
 *     `app/scripts/seed_platform_operator.py`) and returns the real access/refresh pair.
 *   - `PlatformTenantSummary`/`Detail` still return no `plan_code`, no `checklist`, no
 *     `storage_used_bytes`/`storage_quota_bytes` on THIS response — `Tenant` gained the
 *     plan/quota *columns* in this pass, but no platform response schema reads them yet, and
 *     no onboarding-checklist table exists (the checklist is its own live-computed endpoint
 *     now, see `getTenantChecklist` below). These are mapped to `null`, never fabricated.
 *   - Backup-trigger `status` is `"ACKNOWLEDGED"`, not the mock's `"QUEUED"`.
 *   - Audit-log entries carry `before_state`/`after_state` JSONB, not a single `detail`
 *     string.
 *   - **2026-07-13 business-owner walkthrough follow-up**: `POST /platform/tenants` no
 *     longer accepts `owner_password` at all — it issues a real one-time credential-setup
 *     token for the new OWNER instead (the same invite mechanism `POST /users` uses). See
 *     `createTenant()` below. `GET /platform/tenants/{id}/checklist` and `GET /platform/stats`
 *     are both now real endpoints too (`getTenantChecklist`/`getPlatformStats` below), and
 *     `POST /platform/tenants/{id}/impersonate` issues a real, short-lived, audit-logged
 *     tenant-realm session (`impersonateTenant` below). `Tenant` gained `plan_code`/
 *     `storage_quota_bytes`/`branch_limit`/`ecommerce_enabled` columns in this same pass, but
 *     — verified directly against `backend/app/schemas/platform.py` — none of the platform
 *     tenant response schemas (`PlatformTenantSummary`/`PlatformTenantDetail`) surface them
 *     yet, so `plan_code`/storage fields below are still honestly mapped to `null`, not
 *     fabricated from the now-existing columns.
 *
 * Auth/token handling: this app's `PlatformApiClient` interface methods (besides
 * `requestLogin`/`verifyMfa`/`logout`) take no token parameter — pages call
 * `usePlatformClient().listTenants()` etc. directly. So, unlike `dashboard-admin`'s
 * `AuthContext` (which owns `ensureFreshToken()` and every `api/*Api.ts` call site awaits
 * it), THIS client owns its own session read/refresh internally, via the same
 * `lib/storage.ts` `sl-platform-admin-session` key `PlatformAuthContext` already
 * reads/writes — single source of truth, no session state duplicated between the two. Every
 * authenticated method below calls `ensureFreshToken()` first.
 */
import { asDecimalString } from "@shop-ledger/shared-types";
import { clearPlatformSession, loadPlatformSession, savePlatformSession } from "../lib/storage";
import { ApiError, platformApiRequest } from "./httpClient";
import { buildMockHealthNodes } from "./mockHealthNodes";
import type { PlatformApiClient } from "./PlatformApiClient";
import type {
  BackupTrigger,
  DemoResetResult,
  HealthNode,
  ImpersonationSession,
  LiveOnboardingChecklist,
  LogEntry,
  LogFilter,
  MarketplaceOrderFilters,
  MarketplaceSuborderStatusUpdateInput,
  MfaChallenge,
  OnboardingChecklist,
  OnboardingSubmission,
  PaymentReconciliationQueueItem,
  PlatformAuditLogEntry,
  PlatformGeneralSettings,
  PlatformGeneralSettingsUpdate,
  PlatformMarketplaceOrderDetail,
  PlatformMarketplaceOrderSummary,
  PlatformMarketplaceSuborderDetail,
  PlatformSession,
  PlatformStats,
  PlatformTenantDetail,
  PlatformTenantSummary,
  QueueAlertSummary,
  SendTestEmailResult,
  StorageQuotaWarning,
  SyncConflictQueueItem,
  TenantCreationResult,
} from "./types";

const REFRESH_SKEW_MS = 30_000;

// ---------------------------------------------------------------------------------
// Session read/refresh — see this file's top doc comment for why this lives here rather
// than in `PlatformAuthContext`.
// ---------------------------------------------------------------------------------

let refreshInFlight: Promise<string> | null = null;

async function ensureFreshToken(): Promise<string> {
  const current = loadPlatformSession();
  if (!current) throw new Error("Not signed in.");

  const msUntilExpiry = new Date(current.expires_at).getTime() - Date.now();
  if (msUntilExpiry > REFRESH_SKEW_MS) return current.access_token;

  if (!current.refresh_token) {
    // A mock-issued session (no refresh_token) is somehow still in storage while this real
    // client is active — e.g. `VITE_PLATFORM_API_MODE` was flipped mid-session. Nothing to
    // refresh; force a real re-login.
    clearPlatformSession();
    throw new Error("Session expired — please sign in again.");
  }

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const rotated = await platformApiRequest<{
          access_token: string;
          refresh_token: string;
          token_type: string;
          expires_at: string;
        }>("/auth/refresh", { method: "POST", body: { refresh_token: current.refresh_token } });
        const next: PlatformSession = {
          operator: current.operator,
          access_token: rotated.access_token,
          refresh_token: rotated.refresh_token,
          expires_at: rotated.expires_at,
        };
        savePlatformSession(next);
        return next.access_token;
      } catch (error) {
        // Refresh token itself expired/denylisted — the session isn't salvageable.
        clearPlatformSession();
        throw error instanceof Error ? error : new Error("Session expired — please sign in again.");
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

// ---------------------------------------------------------------------------------
// Backend response shapes (`backend/app/schemas/platform.py`), read directly — kept local
// to this file since they're wire shapes, not this app's own domain types.
// ---------------------------------------------------------------------------------

interface BackendOperator {
  id: string;
  email: string;
  display_name: string;
  mfa_enrolled: boolean;
}

interface BackendTenantSummary {
  id: string;
  business_name: string;
  business_type: string | null;
  currency: string;
  is_suspended: boolean;
  is_demo: boolean;
  created_at: string;
  branch_count: number;
  active_user_count: number;
  last_transaction_at: string | null;
  turnover_all_time: string;
  turnover_last_30d: string;
  onboarding_complete: boolean;
}

interface BackendTenantDetail extends BackendTenantSummary {
  usage: { branch_count: number; active_user_count: number; sync_events_last_30d: number };
}

function toTenantSummary(row: BackendTenantSummary): PlatformTenantSummary {
  return {
    id: row.id,
    business_name: row.business_name,
    business_type: row.business_type,
    currency: row.currency,
    is_suspended: row.is_suspended,
    is_demo: row.is_demo,
    // `Tenant.plan_code` exists as a real column since 2026-07-13, but this response shape
    // doesn't read/return it yet — never fabricated. See
    // `PlatformTenantSummary.plan_code`'s doc comment in `api/types.ts`.
    plan_code: null,
    created_at: row.created_at,
    last_transaction_at: row.last_transaction_at,
    turnover_all_time: asDecimalString(row.turnover_all_time),
    turnover_last_30d: asDecimalString(row.turnover_last_30d),
    onboarding_complete: row.onboarding_complete,
  };
}

function toTenantDetail(row: BackendTenantDetail): PlatformTenantDetail {
  return {
    ...toTenantSummary(row),
    usage: {
      branch_count: row.usage.branch_count,
      active_users: row.usage.active_user_count,
      // No MinIO admin API integration — never fabricated. See
      // `PlatformTenantUsage.storage_used_bytes`'s doc comment in `api/types.ts`.
      storage_used_bytes: null,
      storage_quota_bytes: null,
      sync_events_last_30d: row.usage.sync_events_last_30d,
    },
    // No onboarding-checklist table exists — never fabricated. See
    // `PlatformTenantDetail.checklist`'s doc comment in `api/types.ts`.
    checklist: null,
  };
}

async function fetchTenantDetail(token: string, tenantId: string): Promise<PlatformTenantDetail> {
  const row = await platformApiRequest<BackendTenantDetail>(`/tenants/${tenantId}`, { accessToken: token });
  return toTenantDetail(row);
}

export const realPlatformApiClient: PlatformApiClient = {
  async requestLogin(email, password) {
    const res = await platformApiRequest<{ challenge_id: string; expires_at: string }>("/auth/login", {
      method: "POST",
      body: { email, password },
    });
    // Deliberately no `demo_code` — the real challenge response carries no such field at
    // all (see `MfaChallenge.demo_code`'s doc comment in `api/types.ts`).
    const challenge: MfaChallenge = { challenge_id: res.challenge_id };
    return challenge;
  },

  async verifyMfa(challengeId, code) {
    const res = await platformApiRequest<{
      access_token: string;
      refresh_token: string;
      token_type: string;
      expires_at: string;
      operator: BackendOperator;
    }>("/auth/mfa/verify", { method: "POST", body: { challenge_id: challengeId, code } });
    const session: PlatformSession = {
      operator: res.operator,
      access_token: res.access_token,
      refresh_token: res.refresh_token,
      expires_at: res.expires_at,
    };
    return session;
  },

  async logout(session) {
    if (!session.refresh_token) return; // nothing real to revoke
    try {
      await platformApiRequest<void>("/auth/logout", { method: "POST", body: { refresh_token: session.refresh_token } });
    } catch {
      // Best-effort, same convention as `dashboard-admin/src/auth/authApi.ts::logout` — an
      // offline/failed logout call still clears the local session; the refresh token stays
      // valid server-side until it naturally expires.
    }
  },

  /**
   * **2026-07-13**: `POST /platform/tenants` no longer accepts (or needs) `owner_password` —
   * verified directly against `backend/app/schemas/platform.py::PlatformTenantCreateRequest`,
   * which no longer even has the field. It now issues a real one-time `CredentialSetupToken`
   * for the new OWNER (the same invite mechanism `POST /users` uses) and returns it once in
   * `PlatformTenantCreateResponse.setup_token` — this method threads that through as
   * `TenantCreationResult.setup_token` so the wizard can hand it to the business owner
   * honestly (no real email sending exists in this backend; the response's own `note` field
   * says so, surfaced verbatim).
   */
  async createTenant(input): Promise<TenantCreationResult> {
    const token = await ensureFreshToken();
    const created = await platformApiRequest<{
      tenant: BackendTenantSummary;
      owner_user_id: string;
      owner_email: string;
      setup_token: string;
      setup_token_expires_at: string;
      note: string;
    }>("/tenants", {
      method: "POST",
      accessToken: token,
      body: {
        business_name: input.business_name,
        business_type: input.business_type,
        currency: input.currency,
        branch_name: input.branch_name,
        owner_email: input.owner_email,
      },
    });
    // The create route's response nests only a tenant summary (no `usage`) — fetch full
    // detail so this method's return shape genuinely reflects the tenant/branch/owner just
    // created, rather than guessing at zeroed usage the way the mock client does.
    const detail = await fetchTenantDetail(token, created.tenant.id);
    return {
      tenant: detail,
      owner_user_id: created.owner_user_id,
      owner_email: created.owner_email,
      setup_token: created.setup_token,
      setup_token_expires_at: created.setup_token_expires_at,
      note: created.note,
    };
  },

  async getOnboardingChecklist() {
    // The client-side "mark complete" demo state machine has no real backend equivalent to
    // mutate against — use `getTenantChecklist` below for the real, read-only checklist.
    // Not called by any page against this client (only the mock exercises it, for its own
    // interactive nicety — see `PlatformApiClient.ts`'s doc comment).
    throw new Error(
      "This is the mock client's interactive checklist demo — the real backend has no mutable checklist state. Use getTenantChecklist() instead.",
    );
  },

  async advanceOnboardingStep(): Promise<OnboardingChecklist> {
    throw new Error(
      "This is the mock client's interactive checklist demo — the real backend has no mutable checklist state. Use getTenantChecklist() instead.",
    );
  },

  /**
   * `GET /platform/tenants/{id}/checklist` — real, computed live from actual
   * branch/product/sale counts on every read (`backend/app/schemas/platform.py::
   * OnboardingChecklistOut`), not a stored/faked table. Closes the business-owner
   * walkthrough's §9.2-step-4 gap for real.
   */
  async getTenantChecklist(tenantId): Promise<LiveOnboardingChecklist> {
    const token = await ensureFreshToken();
    return platformApiRequest<LiveOnboardingChecklist>(`/tenants/${tenantId}/checklist`, { accessToken: token });
  },

  async listHealthNodes(): Promise<HealthNode[]> {
    // Every node here has no real backend signal (see `mockHealthNodes.ts`'s doc comment) —
    // but the "worker queue depth" node's number CAN be made real for free, since
    // `/queues/sync-conflicts` already exists: fetch it and feed the real count into an
    // otherwise-simulated node, rather than a hardcoded fixture number. Still `mocked: true`
    // (the node's healthy/degraded *status* threshold and every other node's content is
    // simulated) — SystemHealthPage badges it accordingly either way.
    let queueDepth = 0;
    try {
      const token = await ensureFreshToken();
      const conflicts = await platformApiRequest<unknown[]>("/queues/sync-conflicts", {
        accessToken: token,
        query: { limit: 1000 },
      });
      queueDepth = conflicts.length;
    } catch {
      // Health grid shouldn't fail entirely just because this one nicety couldn't load.
    }
    return buildMockHealthNodes(queueDepth);
  },

  async searchLogs(_filter: LogFilter): Promise<LogEntry[]> {
    // No structured-log pipeline exists server-side (no correlation-id middleware, no
    // Loki/ELK-class stack — see BACKEND_ARCHITECTURE.md's "What's deferred"). Always
    // empty; `LogsPage` renders a permanent "not available" note in real mode rather than
    // an empty-looking-like-"no matches" table.
    return [];
  },

  async listSyncConflicts(): Promise<SyncConflictQueueItem[]> {
    const token = await ensureFreshToken();
    return platformApiRequest<SyncConflictQueueItem[]>("/queues/sync-conflicts", {
      accessToken: token,
      query: { limit: 200 },
    });
  },

  async listPendingReconciliations(): Promise<PaymentReconciliationQueueItem[]> {
    const token = await ensureFreshToken();
    const rows = await platformApiRequest<
      Array<Omit<PaymentReconciliationQueueItem, "amount"> & { amount: string }>
    >("/queues/payment-pending", { accessToken: token, query: { limit: 200 } });
    return rows.map((row) => ({ ...row, amount: asDecimalString(row.amount) }));
  },

  async getQueueAlertSummary(): Promise<QueueAlertSummary> {
    const token = await ensureFreshToken();
    return platformApiRequest<QueueAlertSummary>("/queues/alert-summary", { accessToken: token });
  },

  async listStorageQuotaWarnings(): Promise<StorageQuotaWarning[]> {
    // `Tenant` has real quota columns since 2026-07-13, but no route aggregates real
    // storage-usage bytes against them (no MinIO admin API integration) — there is nothing
    // to query. Always empty; `QueuesPage` carries a permanent `MockBadge` "not available" note on this panel
    // regardless of which client is active, so an empty array here is never misread as "0
    // tenants are near quota" (a real, if reassuring, fact) rather than "unknown."
    return [];
  },

  async listTenants(): Promise<PlatformTenantSummary[]> {
    const token = await ensureFreshToken();
    const rows = await platformApiRequest<BackendTenantSummary[]>("/tenants", { accessToken: token });
    return rows.map(toTenantSummary);
  },

  async getTenant(tenantId): Promise<PlatformTenantDetail> {
    const token = await ensureFreshToken();
    return fetchTenantDetail(token, tenantId);
  },

  async suspendTenant(tenantId, reason): Promise<PlatformTenantDetail> {
    const token = await ensureFreshToken();
    const row = await platformApiRequest<BackendTenantDetail>(`/tenants/${tenantId}/suspend`, {
      method: "PATCH",
      accessToken: token,
      body: { reason: reason || undefined },
    });
    return toTenantDetail(row);
  },

  async reactivateTenant(tenantId): Promise<PlatformTenantDetail> {
    const token = await ensureFreshToken();
    const row = await platformApiRequest<BackendTenantDetail>(`/tenants/${tenantId}/reactivate`, {
      method: "PATCH",
      accessToken: token,
    });
    return toTenantDetail(row);
  },

  async triggerBackup(tenantId): Promise<BackupTrigger> {
    const token = await ensureFreshToken();
    return platformApiRequest<BackupTrigger>(`/tenants/${tenantId}/backup`, { method: "POST", accessToken: token });
  },

  /**
   * "Reset Demo Data" (`TenantDetailPage.tsx`, shown only when `tenant.is_demo`). Backend
   * (`backend/app/api/v1/platform/tenants.py::reset_demo_data`) independently re-checks
   * `is_demo` server-side (403 if not) — this button being hidden client-side is a
   * convenience, not the actual guard, same discipline every role-gated nav item in this
   * project already follows.
   */
  async resetDemoData(tenantId): Promise<DemoResetResult> {
    const token = await ensureFreshToken();
    return platformApiRequest<DemoResetResult>(`/tenants/${tenantId}/reset-demo-data`, {
      method: "POST",
      accessToken: token,
    });
  },

  async listAuditLog(tenantId): Promise<PlatformAuditLogEntry[]> {
    const token = await ensureFreshToken();
    const rows = await platformApiRequest<
      Array<{
        id: string;
        operator_id: string;
        operator_email: string;
        action: string;
        target_tenant_id: string | null;
        before_state: Record<string, unknown> | null;
        after_state: Record<string, unknown> | null;
        created_at: string;
      }>
    >("/audit-log", { accessToken: token, query: { tenant_id: tenantId, limit: 200 } });
    return rows.map((row) => ({
      id: row.id,
      operator_id: row.operator_id,
      operator_email: row.operator_email,
      action: row.action,
      target_tenant_id: row.target_tenant_id,
      timestamp: row.created_at,
      detail: null,
      before_state: row.before_state,
      after_state: row.after_state,
    }));
  },

  /**
   * `GET /platform/stats` — real aggregate counts via the BYPASSRLS platform DB session
   * (`backend/app/schemas/platform.py::PlatformStatsOut`). Closes the business-owner
   * walkthrough's single biggest finding: "platform admin has no usage/statistics
   * dashboard at all."
   */
  async getPlatformStats(): Promise<PlatformStats> {
    const token = await ensureFreshToken();
    const stats = await platformApiRequest<{
      total_tenants: number;
      active_tenants: number;
      suspended_tenants: number;
      total_branches: number;
      total_users: number;
      sales_count_last_30d: number;
      sales_revenue_last_30d: string;
    }>("/stats", { accessToken: token });
    return { ...stats, sales_revenue_last_30d: asDecimalString(stats.sales_revenue_last_30d) };
  },

  /**
   * `POST /platform/tenants/{id}/impersonate` — a real, short-lived (15 min),
   * non-refreshable tenant-realm session, audit-logged server-side (`TENANT_IMPERSONATION_
   * ISSUED`) BEFORE the token is returned. Defaults to the tenant's OWNER when `userId` is
   * omitted. The frontend owns the mandatory "you are viewing as X" banner — see
   * `auth/ImpersonationContext.tsx`.
   */
  async impersonateTenant(tenantId, userId, reason): Promise<ImpersonationSession> {
    const token = await ensureFreshToken();
    const res = await platformApiRequest<{
      access_token: string;
      refresh_token: string | null;
      token_type: string;
      expires_at: string;
      impersonated_user_id: string;
      impersonated_user_email: string;
      tenant_id: string;
      dashboard_handoff_code: string;
    }>(`/tenants/${tenantId}/impersonate`, {
      method: "POST",
      accessToken: token,
      body: { user_id: userId ?? undefined, reason: reason || undefined },
    });
    return {
      access_token: res.access_token,
      token_type: res.token_type,
      expires_at: res.expires_at,
      impersonated_user_id: res.impersonated_user_id,
      impersonated_user_email: res.impersonated_user_email,
      tenant_id: res.tenant_id,
      dashboard_handoff_code: res.dashboard_handoff_code,
    };
  },

  /**
   * `GET /platform/marketplace/orders` — genuinely live: `status` is the backend's
   * live-recomputed rollup (`marketplace_service.compute_rollup_status`), passed straight
   * through unchanged (no client-side remapping needed, unlike the tenant-field-
   * unavailability cases above — every field on this response is real).
   */
  async listMarketplaceOrders(filters: MarketplaceOrderFilters = {}): Promise<PlatformMarketplaceOrderSummary[]> {
    const token = await ensureFreshToken();
    return platformApiRequest<PlatformMarketplaceOrderSummary[]>("/marketplace/orders", {
      accessToken: token,
      query: {
        status: filters.status,
        since: filters.since,
        until: filters.until,
        limit: filters.limit,
      },
    });
  },

  async getMarketplaceOrder(orderId): Promise<PlatformMarketplaceOrderDetail> {
    const token = await ensureFreshToken();
    return platformApiRequest<PlatformMarketplaceOrderDetail>(`/marketplace/orders/${orderId}`, {
      accessToken: token,
    });
  },

  /**
   * `PATCH /platform/marketplace/orders/{orderId}/suborders/{suborderId}` — an illegal
   * `status` transition 409s (`ApiError.status === 409`); this method doesn't catch or
   * reinterpret that, it propagates straight through `platformApiRequest`'s `ApiError` for
   * the caller (`MarketplaceOrderDetailPage`) to surface, same as every other mutating call
   * in this client.
   */
  async updateMarketplaceSuborderStatus(
    orderId,
    suborderId,
    body: MarketplaceSuborderStatusUpdateInput,
  ): Promise<PlatformMarketplaceSuborderDetail> {
    const token = await ensureFreshToken();
    return platformApiRequest<PlatformMarketplaceSuborderDetail>(
      `/marketplace/orders/${orderId}/suborders/${suborderId}`,
      { method: "PATCH", accessToken: token, body },
    );
  },

  /** `GET /platform/settings/general` (`backend/app/schemas/platform.py::PlatformSettingsOut`). */
  async getGeneralSettings(): Promise<PlatformGeneralSettings> {
    const token = await ensureFreshToken();
    return platformApiRequest<PlatformGeneralSettings>("/settings/general", { accessToken: token });
  },

  /** `PATCH /platform/settings/general` — `body` is sent as-is; omitted keys stay untouched
   * server-side (see `PlatformGeneralSettingsUpdate`'s doc comment in `types.ts`). */
  async updateGeneralSettings(body: PlatformGeneralSettingsUpdate): Promise<PlatformGeneralSettings> {
    const token = await ensureFreshToken();
    return platformApiRequest<PlatformGeneralSettings>("/settings/general", {
      method: "PATCH",
      accessToken: token,
      body,
    });
  },

  /** `POST /platform/settings/general/test-email` — always resolves 200; a send failure
   * comes back as `{sent: false, detail: "..."}`, not an `ApiError`. */
  async sendTestEmail(to: string): Promise<SendTestEmailResult> {
    const token = await ensureFreshToken();
    return platformApiRequest<SendTestEmailResult>("/settings/general/test-email", {
      method: "POST",
      accessToken: token,
      body: { to },
    });
  },
};

export { ApiError };
